// Block I (UI-16): the Team dashboard's task board, list and by-project views. Presentational:
// TeamDashboard.js owns the tasks, the filters and every request; these only draw what they are
// handed and call back.
//
// Every move has a tap route: "Move to…" on each card you can change opens the kit's MoveToSheet
// (addendum 7). Block M2 (UI-16b): a card you can change can also be DRAGGED to another column, with
// a mouse, or on a touch screen after a long press (useBoardDrag); the drop goes through the same
// update as "Move to…", which stays as the tap and keyboard route.
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, AvatarStack, Button, Card, EmptyState, StatusPill } from '../../ui';
import useBoardDrag from './useBoardDrag';
import { formatTaskDay } from '../../utils/taskTime';
import { personTint } from './teamPeople';
import { MoveCountsProvider, MovedTag } from '../task/MovedTag';   // 2026-10-21: "Moved 3×"

export const STATUS_LABEL = { todo: 'To do', overdue: 'Overdue', done: 'Done' };
export const STATUS_TONE = { todo: 'pending', overdue: 'warning', done: 'success' };
export const COLUMN_TINT = { todo: 'var(--pastel-yellow)', overdue: 'var(--pastel-coral)', done: 'var(--pastel-mint)' };

// The task's own priority word, as the kit's priority pill takes it.
export function priorityKey(p) {
  const k = String(p || '').toLowerCase();
  return ['low', 'medium', 'high'].includes(k) ? k : null;
}

// "Due 9 Oct", from the deadline, else the start. '' when the task has no date.
export function dueText(task) {
  const day = formatTaskDay(task?.deadline || task?.datetime || task?.date);
  return day ? `Due ${day}` : '';
}

// The people on a task, as avatars. A button (the reassign picker) when the caller may change it.
export function People({ task, people, canEdit, onReassign, size = 24 }) {
  const stack = people.length
    ? <AvatarStack size={size} max={3} people={people.map((u) => ({ name: u.name, color: personTint(u.id) }))} />
    : <span className="td-line__sub">No one</span>;
  const names = people.map((u) => u.name).join(', ') || 'no one';
  if (!canEdit) return <span className="td-who" title={`Assigned to ${names}`}>{stack}</span>;
  return (
    <button type="button" className="td-who" aria-label={`Reassign "${task.title}", assigned to ${names}`} onClick={() => onReassign(task)}>
      {stack}
    </button>
  );
}

// Where a card dropped on a column goes. Only Done is a state the person sets: Overdue is worked out
// from the due date, so a drop that would need the date changed moves nothing and says why.
// Returns { dest: 'todo' | 'done' | null, note }.
export function dropDestination(status, toColumn) {
  if (toColumn === status) return { dest: null };
  if (toColumn === 'done') return { dest: 'done' };
  if (status === 'done') {
    return { dest: 'todo', note: toColumn === 'overdue' ? 'Reopened. It shows under Overdue only while it is past its due date.' : undefined };
  }
  return { dest: null, note: 'Overdue follows the due date. Change the date to move it between To do and Overdue.' };
}

function TaskCard({ task, status, people, canEdit, onOpen, onMove, onReassign, dragProps, dragging }) {
  const pk = priorityKey(task.priority);
  const due = dueText(task);
  return (
    <article className={`td-card${status === 'done' ? ' td-card--done' : ''}${dragProps ? ' td-card--draggable' : ''}${dragging ? ' td-card--dragging' : ''}`}
      {...(dragProps || {})}>
      <button type="button" className="td-card__open" onClick={() => onOpen(task)}>
        <span className="td-card__title">{task.title}</span>
      </button>
      {(due || pk) && (
        <div className="td-card__meta">
          {due && <StatusPill tone={status === 'overdue' ? 'warning' : 'neutral'}>{due}</StatusPill>}
          {pk && <StatusPill priority={pk} />}
          <MovedTag task={task} />
        </div>
      )}
      <div className="td-card__foot">
        <People task={task} people={people} canEdit={canEdit} onReassign={onReassign} />
        {canEdit && (
          <Button size="sm" variant="tertiary" aria-label={`Move "${task.title}" to…`} onClick={() => onMove(task)}>Move to…</Button>
        )}
      </div>
    </article>
  );
}

// Carla's item 4 (2026-10-12): 15 at a time. The page numbers and the total are the server's
// (useTaskPage), so "Page 2 of 3" is true of the whole list, not of what is on screen.
export function Pager({ label, page, pages, onPage, disabled }) {
  if (!pages || pages <= 1) return null;
  return (
    <nav className="td-pager" aria-label={`${label}: pages`}>
      <Button size="sm" variant="tertiary" aria-label={`Previous page of ${label}`} disabled={disabled || page <= 1} onClick={() => onPage(page - 1)}>← Previous</Button>
      <span className="td-pager__at">Page {page} of {pages}</span>
      <Button size="sm" variant="tertiary" aria-label={`Next page of ${label}`} disabled={disabled || page >= pages} onClick={() => onPage(page + 1)}>Next →</Button>
    </nav>
  );
}

// columns: [{ key: 'todo' | 'overdue' | 'done', tasks, total, page, pages, onPage, loading, error, onRetry }]
// onDrop(task, toColumn): a card was dragged onto another column (Block M2). Without it, no dragging.
export function BoardView({ columns, statusOf, peopleOf, canEdit, onOpen, onMove, onReassign, onDrop }) {
  const boardRef = useRef(null);
  const { drag, cardProps } = useBoardDrag({ onDrop: (t, to) => onDrop && onDrop(t, to), boardRef });
  return (
    <MoveCountsProvider tasks={columns.flatMap((c) => c.tasks || [])}>
    <div className={`td-board${drag ? ' td-board--dragging' : ''}`} ref={boardRef}>
      {columns.map((col) => (
        <section key={col.key} className={`td-col${drag && drag.over === col.key && drag.from !== col.key ? ' td-col--over' : ''}`}
          data-drop-column={col.key} aria-label={STATUS_LABEL[col.key]} aria-busy={col.loading || undefined} style={{ '--td-col': COLUMN_TINT[col.key] }}>
          <div className="td-col__head">
            <h3 className="td-col__title">{STATUS_LABEL[col.key]}</h3>
            <StatusPill tone="neutral">{col.total ?? col.tasks.length}</StatusPill>
          </div>
          <div className="td-col__body">
            {col.error ? (
              <div className="td-col__failed" role="alert">
                <p className="td-failed">{col.error}</p>
                {col.onRetry && <Button size="sm" variant="secondary" onClick={col.onRetry}>Try again</Button>}
              </div>
            ) : col.tasks.length === 0
              ? <p className="td-col__empty">{col.loading ? 'Loading…' : 'Nothing here.'}</p>
              : col.tasks.map((t) => {
                const editable = canEdit(t);
                return (
                  <TaskCard key={t.id} task={t} status={statusOf(t)} people={peopleOf(t)} canEdit={editable}
                    onOpen={onOpen} onMove={onMove} onReassign={onReassign}
                    dragProps={onDrop && editable ? cardProps(t, col.key, t.title) : null}
                    dragging={drag?.item?.id === t.id} />
                );
              })}
          </div>
          <Pager label={STATUS_LABEL[col.key]} page={col.page} pages={col.pages} onPage={col.onPage} disabled={col.loading} />
        </section>
      ))}
      {drag && typeof document !== 'undefined' && createPortal(
        <div className="td-ghost" aria-hidden="true" style={{ left: drag.x, top: drag.y }}>
          <span className="td-card__title">{drag.title}</span>
        </div>,
        document.body,
      )}
      {drag && (
        <span className="ui-visually-hidden" role="status">
          {drag.over && drag.over !== drag.from ? `Drop to move "${drag.title}" to ${STATUS_LABEL[drag.over]}.` : `Moving "${drag.title}". Press Escape to cancel.`}
        </span>
      )}
    </div>
    </MoveCountsProvider>
  );
}

function TaskLine({ task, status, people, canEdit, onOpen, onReassign, sub }) {
  const pk = priorityKey(task.priority);
  return (
    <li className="td-line">
      <button type="button" className="td-line__main" onClick={() => onOpen(task)}>
        <span className="td-card__title">{task.title}</span>
        {sub && <span className="td-line__sub">{sub}</span>}
      </button>
      <People task={task} people={people} canEdit={canEdit} onReassign={onReassign} />
      <span className="td-line__pills">
        <MovedTag task={task} />
        {pk && <StatusPill priority={pk} />}
        <StatusPill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusPill>
      </span>
    </li>
  );
}

export function ListView({ tasks, statusOf, peopleOf, canEdit, onOpen, onReassign, emptyAction }) {
  if (tasks.length === 0) {
    return <EmptyState title="No tasks match" action={emptyAction}>Try another member, status or search, or clear the filters.</EmptyState>;
  }
  return (
    <MoveCountsProvider tasks={tasks}>
    <ul className="td-list">
      {tasks.map((t) => (
        <TaskLine key={t.id} task={t} status={statusOf(t)} people={peopleOf(t)} canEdit={canEdit(t)}
          onOpen={onOpen} onReassign={onReassign} sub={[dueText(t), t.taskType].filter(Boolean).join(' · ')} />
      ))}
    </ul>
    </MoveCountsProvider>
  );
}

// groups: [{ key, name, tasks }]
export function ProjectGroups({ groups, statusOf, peopleOf, canEdit, onOpen, onReassign, emptyAction, partial }) {
  if (groups.length === 0) return <EmptyState title="No tasks match" action={emptyAction}>Try another member, status or search, or clear the filters.</EmptyState>;
  return (
    <MoveCountsProvider tasks={groups.flatMap((g) => g.tasks)}>
    <div className="td-groups">
      {groups.map((g, i) => {
        const done = g.tasks.filter((t) => statusOf(t) === 'done').length;
        return (
          <Card key={g.key} lift={false} color={['pastel-blue', 'pastel-lilac', 'pastel-yellow', 'pastel-mint', 'pastel-peach'][i % 5]}
            label={g.name} meta={`${done} of ${g.tasks.length} done${partial ? ' on this page' : ''}`}>
            <ul className="td-list">
              {g.tasks.map((t) => (
                <TaskLine key={t.id} task={t} status={statusOf(t)} people={peopleOf(t)} canEdit={canEdit(t)}
                  onOpen={onOpen} onReassign={onReassign} sub={dueText(t)} />
              ))}
            </ul>
          </Card>
        );
      })}
    </div>
    </MoveCountsProvider>
  );
}

// One person, as the overview lists them.
export function PersonAvatar({ user, size = 36 }) {
  return <Avatar name={user?.name || 'Former member'} size={size} color={personTint(user?.id)} />;
}
